import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { CardContent } from '@/shared/components/card-content/card-content';
import { NgTemplateOutlet } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';

import type { EspecialidadeMedicaDTO, EspecialidadeMedicaResponse } from '@/core/api/sicom-api/servicos/pessoa-fisica/especialidade-medica/especialidade-medica.model';
import type { EspecialidadeMedicaItemDTO } from '@/core/api/sicom-api/servicos/pessoa-fisica/especialidade-medica/especialidade-medica-item.model';
import { EspecialidadeMedicaServicosService } from '@/features/servicos/pessoa-fisica/content/especialidade-medica-servicos/especialidade-medica-servicos.service';
import { formatarDataHoraMascara, formatarDataMascara, montarRequestProtocolo, valorTexto, type ProtocoloResumoConsulta } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';
import { rotuloBadgeSituacao, situacaoClassePorTexto } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface CampoVisualizacao {
    label: string;
    chave: keyof EspecialidadeMedicaDTO;
    formatador?: (valor: unknown) => string;
}

interface SecaoVisualizacao {
    titulo: string;
    campos: CampoVisualizacao[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-especialidade-medica-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, NgTemplateOutlet, CardContent, DialogModule, VisualizadorPdf, InputTextModule],
    templateUrl: './especialidade-medica-servicos.html',
    styleUrl: './especialidade-medica-servicos.scss'
})
export class EspecialidadeMedicaServicos {
    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;
    protected readonly situacaoClassePorTexto = situacaoClassePorTexto;
    protected readonly rotuloBadgeSituacao = rotuloBadgeSituacao;

    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');

    readonly carregando = signal(false);
    readonly erroConsulta = signal<string | null>(null);
    readonly dados = signal<EspecialidadeMedicaResponse | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-especialidade-medica.pdf');

    readonly secoes: SecaoVisualizacao[] = [
        {
            titulo: 'Protocolo',
            campos: [
                { label: 'Número Protocolo', chave: 'numeroProtocolo' },
                { label: 'Ano Protocolo', chave: 'anoProtocolo' },
                { label: 'Data/Hora Solicitacao', chave: 'dataHora', formatador: formatarDataHoraMascara },
                { label: 'Situação', chave: 'situacao', formatador: this.formatarSituacao }
            ]
        },
        {
            titulo: 'Dados do Solicitante',
            campos: [
                { label: 'Nome', chave: 'nome' },
                { label: 'CPF', chave: 'cpf' },
                { label: 'CRM', chave: 'numCrm' },
                { label: 'UF CRM Origem', chave: 'ufCrmOrigem' },
                { label: 'Médico Ativo', chave: 'medicoAtivo' },
                { label: 'Email Solicitante', chave: 'emailSolicitante', formatador: this.formatarEmails }
            ]
        },
        {
            titulo: 'Pagamento',
            campos: [
                { label: 'Valor', chave: 'valor', formatador: this.formatarMoeda },
                { label: 'Data Emissão', chave: 'dataEmissao', formatador: formatarDataMascara },
                { label: 'Data Vencimento', chave: 'dataVencimento', formatador: formatarDataMascara },
                { label: 'Nosso Número', chave: 'nossoNumero' },
                { label: 'Banco', chave: 'banco' },
                { label: 'Número Convênio', chave: 'numeroConvenio' },
                { label: 'Número Documento', chave: 'numeroDocumento' },
                { label: 'Identificador Pagamento', chave: 'identificadorPagamento' }
            ]
        }
    ];

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';

    private readonly servico = inject(EspecialidadeMedicaServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Recarrega ao mudar protocolo/UF; o guard ultimaChaveConsulta evita repetir a mesma consulta.
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    get especialidades(): EspecialidadeMedicaItemDTO[] {
        return this.dados()?.especialidades ?? [];
    }

    /**
     * Campo que sai como selo colorido, e não como valor comum: só a situação do protocolo. Estava
     * repetido em duas condições idênticas no template (a classe do bloco e o `@if` do conteúdo), e
     * bastava uma delas mudar para o selo aparecer sem a cor — ou a cor sem o selo.
     */
    ehCampoBadge(campo: CampoVisualizacao): boolean {
        const label = campo.label.toLowerCase();
        return campo.chave === 'situacao' || label.includes('situacao') || label.includes('situação');
    }

    /**
     * Id do controle de um campo, para o `<label for>` apontar para ele. O id junta seção e rótulo
     * porque os rótulos se repetem entre seções — só o rótulo geraria ids duplicados, e o clique no
     * label levaria o foco para o campo da outra seção.
     */
    idDoCampo(secao: SecaoVisualizacao, campo: CampoVisualizacao): string {
        return `especialidade-${this.emSlug(secao.titulo)}-${this.emSlug(campo.label)}`;
    }

    /**
     * Id de um campo da lista de especialidades solicitadas. Leva o ÍNDICE porque os rótulos se
     * repetem a cada item: sem ele, clicar no rótulo da segunda especialidade focaria a primeira.
     */
    idDoCampoDaEspecialidade(indice: number, rotulo: string): string {
        return `especialidade-item-${indice}-${this.emSlug(rotulo)}`;
    }

    /** Texto reduzido a `a-z0-9-`, para servir de id de elemento. */
    private emSlug(texto: string): string {
        return texto
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    }

    valorCampo(campo: CampoVisualizacao): string {
        const dados = this.dados();
        if (!dados) {
            return '-';
        }

        const valorBruto = dados[campo.chave];
        if (valorBruto === null || valorBruto === undefined || valorTexto(valorBruto).trim() === '') {
            return '-';
        }

        if (campo.formatador) {
            const valorFormatado = campo.formatador(valorBruto);
            return valorFormatado ? valorFormatado : '-';
        }

        return valorTexto(valorBruto);
    }

    valorCampoEspecialidade(item: EspecialidadeMedicaItemDTO, chave: keyof EspecialidadeMedicaItemDTO): string {
        const valor = item[chave];
        if (valor === null || valor === undefined) {
            return '-';
        }

        if (chave === 'dataExpedicaoDiploma') {
            const dataFormatada = formatarDataMascara(valor);
            return dataFormatada || '-';
        }

        const texto = valorTexto(valor).trim();
        return texto || '-';
    }

    possuiAnexo(item: EspecialidadeMedicaItemDTO): boolean {
        return !!valorTexto(item.linkAnexo).trim();
    }

    onVisualizarAnexo(event: Event, item: EspecialidadeMedicaItemDTO): void {
        event.preventDefault();
        const idAnexo = this.extrairIdAnexo(item.linkAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dados.set(null);
            this.carregando.set(false);
            this.erroConsulta.set(null);
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;
        this.carregando.set(true);
        this.erroConsulta.set(null);

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dados.set(response ?? null);
                    this.carregando.set(false);
                },
                error: () => {
                    this.dados.set(null);
                    this.carregando.set(false);
                    this.erroConsulta.set('Falha ao carregar os dados de registro de especialidade.');
                }
            });
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const texto = valorTexto(campoAnexo).trim();
        if (!texto) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(texto);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(texto, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e segue fallback.
        }

        if (/^[A-Za-z0-9_-]+$/.test(texto)) {
            return texto;
        }

        return null;
    }

    private formatarMoeda(valor: unknown): string {
        const numero = Number(valor);
        if (Number.isFinite(numero)) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numero);
        }

        const texto = valorTexto(valor).trim();
        if (!texto) {
            return '';
        }

        const normalizado = Number(texto.replace(/\./g, '').replace(',', '.'));
        if (Number.isFinite(normalizado)) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(normalizado);
        }

        return texto;
    }

    private formatarSituacao(valor: unknown): string {
        const codigo = valorTexto(valor).trim().toUpperCase();
        switch (codigo) {
            case 'A':
                return 'Ativo';
            case 'T':
                return 'Tramitado';
            case 'P':
                return 'Pendência';
            case 'C':
                return 'Cancelado';
            case 'E':
                return 'Encerrado';
            default:
                return valorTexto(valor);
        }
    }

    private formatarEmails(valor: unknown): string {
        const texto = valorTexto(valor).trim();
        if (!texto) {
            return '';
        }

        return texto
            .split(';')
            .map((email) => email.trim())
            .filter((email) => email.length > 0)
            .join('\n');
    }
}
